import {
  type CompleteOnboardingTaskRequest,
  type NotApplicableOnboardingTaskRequest,
  type OnboardingTaskListQuery,
  type ReassignOnboardingTaskRequest,
  type RunOnboardingSystemActionRequest,
  type CancelOnboardingTaskRequest,
  type SkipOnboardingTaskRequest,
  onboardingSystemActionTargetsSchema,
  onboardingTaskListResponseSchema,
  onboardingTaskMutationResponseSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const onboardingTasksKey = (query: OnboardingTaskListQuery) =>
  ['onboarding', 'tasks', query] as const;

export function useOnboardingTasks(query: OnboardingTaskListQuery) {
  return useQuery({
    queryKey: onboardingTasksKey(query),
    queryFn: () =>
      apiFetch(
        `/onboarding/tasks${toSearchParams({
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          status: query.status,
        })}`,
        onboardingTaskListResponseSchema,
      ),
  });
}

export function useCompleteOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: CompleteOnboardingTaskRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/complete`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

function invalidateOnboarding(client: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['onboarding', 'tasks'] }),
    client.invalidateQueries({ queryKey: ['onboarding', 'cases'] }),
    client.invalidateQueries({ queryKey: ['onboarding', 'case'] }),
  ]);
}

export function useClaimOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, revision }: { taskId: string; revision: number }) =>
      apiFetch(`/onboarding/tasks/${taskId}/claim`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify({ revision }),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useUnclaimOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, revision }: { taskId: string; revision: number }) =>
      apiFetch(`/onboarding/tasks/${taskId}/unclaim`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify({ revision }),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useSkipOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: SkipOnboardingTaskRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/skip`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useCancelOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: CancelOnboardingTaskRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/cancel`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useReopenOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: { revision: number } }) =>
      apiFetch(`/onboarding/tasks/${taskId}/reopen`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useNotApplicableOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: NotApplicableOnboardingTaskRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/not-applicable`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useReassignOnboardingTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: ReassignOnboardingTaskRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/reassign`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}

export function useOnboardingSystemActionTargets(taskId: string, enabled: boolean, q?: string) {
  return useQuery({
    queryKey: ['onboarding', 'system-action-targets', taskId, q ?? ''],
    enabled,
    queryFn: () => {
      const search = new URLSearchParams();
      if (q) {
        search.set('q', q);
      }
      const suffix = search.size > 0 ? `?${search.toString()}` : '';
      return apiFetch(
        `/onboarding/tasks/${taskId}/system-action-targets${suffix}`,
        onboardingSystemActionTargetsSchema,
      );
    },
  });
}

export function useRunOnboardingSystemAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: RunOnboardingSystemActionRequest }) =>
      apiFetch(`/onboarding/tasks/${taskId}/system-action`, onboardingTaskMutationResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await invalidateOnboarding(client);
    },
  });
}
